import { useState } from "react";
import type { Recurrence } from "../../api/types";
import { WEEKDAYS_LETTER, WEEKDAYS_LONG, addMonths, daysInMonth, parts, weekday, type DateStr } from "../../lib/date";
import { WEEKDAY_CODES, nthOfMonth } from "../../lib/recurrence";
import { DateSelect } from "../ui/DateSelect";
import "./overlays.css";

const ORDINAL = ["", "primeira", "segunda", "terceira", "quarta"];

/** "Recorrência personalizada" dialog. */
export function RecurrenceDialog({
  start,
  value,
  today,
  weekStart,
  onDone,
  onCancel
}: {
  start: DateStr;
  value: Recurrence | null;
  today: DateStr;
  weekStart: number;
  onDone: (rule: Recurrence) => void;
  onCancel: () => void;
}) {
  const dow = weekday(start);
  const nth = nthOfMonth(start);
  const [frequency, setFrequency] = useState<Recurrence["frequency"]>(value?.frequency ?? "weekly");
  const [interval, setInterval] = useState(value?.interval ?? 1);
  const [days, setDays] = useState<string[]>(value?.by_weekday?.length && value.frequency === "weekly" ? value.by_weekday : [WEEKDAY_CODES[dow]]);
  const [monthly, setMonthly] = useState<"day" | "nth" | "last" | "lastDay">(
    value?.by_month_day === -1 ? "lastDay" : value?.by_set_pos === -1 ? "last" : value?.by_set_pos ? "nth" : "day"
  );
  const { y, m, d } = parts(start);
  const isMonthEnd = d === daysInMonth(y, m);
  const [ends, setEnds] = useState<"never" | "date" | "count">(value?.end_date ? "date" : value?.count ? "count" : "never");
  const [endDate, setEndDate] = useState(value?.end_date ?? addMonths(start, 3));
  const [count, setCount] = useState(value?.count ?? 13);
  const order = Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);

  const build = (): Recurrence => {
    const rule: Recurrence = { frequency };
    if (interval > 1) rule.interval = interval;
    if (frequency === "weekly") rule.by_weekday = WEEKDAY_CODES.filter((c) => days.includes(c));
    if (frequency === "monthly" && monthly === "lastDay") {
      rule.by_month_day = -1;
    } else if (frequency === "monthly" && monthly !== "day") {
      rule.by_weekday = [WEEKDAY_CODES[dow]];
      rule.by_set_pos = monthly === "last" ? -1 : nth.pos;
    }
    if (ends === "date") rule.end_date = endDate;
    if (ends === "count") rule.count = count;
    return rule;
  };

  return (
    <div className="scrim" onPointerDown={(e) => e.target === e.currentTarget && onCancel()} data-keep-open>
      <div className="dialog rec-dialog" role="dialog" aria-label="Recorrência personalizada">
        <h2>Recorrência personalizada</h2>
        <div className="rec-line">
          <span>Repetir a cada</span>
          <input type="number" min={1} max={99} className="field filled num" value={interval} onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))} />
          <select className="chip-select" value={frequency} onChange={(e) => setFrequency(e.target.value as Recurrence["frequency"])}>
            <option value="daily">{interval > 1 ? "dias" : "dia"}</option>
            <option value="weekly">{interval > 1 ? "semanas" : "semana"}</option>
            <option value="monthly">{interval > 1 ? "meses" : "mês"}</option>
            <option value="yearly">{interval > 1 ? "anos" : "ano"}</option>
          </select>
        </div>

        {frequency === "weekly" && (
          <div className="rec-block">
            <div className="rec-label">Repetir</div>
            <div className="rec-days">
              {order.map((i) => {
                const code = WEEKDAY_CODES[i];
                const on = days.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    className={`rec-day${on ? " on" : ""}`}
                    aria-pressed={on}
                    aria-label={WEEKDAYS_LONG[i]}
                    onClick={() => setDays(on ? (days.length > 1 ? days.filter((d) => d !== code) : days) : [...days, code])}
                  >
                    {WEEKDAYS_LETTER[i]}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {frequency === "monthly" && (
          <div className="rec-block">
            <select className="chip-select" value={monthly} onChange={(e) => setMonthly(e.target.value as typeof monthly)}>
              <option value="day">Mensal no dia {Number(start.slice(8, 10))}</option>
              {nth.pos <= 4 && (
                <option value="nth">
                  Mensal na {ORDINAL[nth.pos]} {WEEKDAYS_LONG[dow]}
                </option>
              )}
              {nth.isLast && <option value="last">Mensal na última {WEEKDAYS_LONG[dow]}</option>}
              {(isMonthEnd || monthly === "lastDay") && <option value="lastDay">Mensal no último dia do mês</option>}
            </select>
          </div>
        )}

        <div className="rec-block">
          <div className="rec-label">Termina em</div>
          <label className="rec-radio">
            <input type="radio" name="ends" checked={ends === "never"} onChange={() => setEnds("never")} /> Nunca
          </label>
          <label className="rec-radio">
            <input type="radio" name="ends" checked={ends === "date"} onChange={() => setEnds("date")} /> Em
            <span className={ends === "date" ? "" : "disabled"}>
              <DateSelect value={endDate} today={today} weekStart={weekStart} ariaLabel="Termina em" onChange={(d) => { setEndDate(d); setEnds("date"); }} />
            </span>
          </label>
          <label className="rec-radio">
            <input type="radio" name="ends" checked={ends === "count"} onChange={() => setEnds("count")} /> Após
            <input type="number" min={1} max={730} className="field filled num" value={count} onFocus={() => setEnds("count")} onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} />
            ocorrências
          </label>
        </div>

        <div className="actions">
          <button className="btn text" onClick={onCancel}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => onDone(build())}>
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
}
